import { useCallback, useEffect, useState } from 'react';
import {
  createEmptyCompanyEntry,
  diffSkillsForPayload,
  normalizeCompaniesForEditing,
  sanitizeCompaniesForPayload
} from '../utils/profileTransforms';

export const useEditableProfile = (initialPerson, skillSnapshot, initialCompanyEntries = []) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(initialPerson?.description ?? '');
  const [editedExperience, setEditedExperience] = useState(initialPerson?.experience ?? '');
  const [editedRole, setEditedRole] = useState(initialPerson?.role ?? '');
  const [editableSkills, setEditableSkills] = useState(skillSnapshot?.list ?? []);
  const [companyEntries, setCompanyEntries] = useState(normalizeCompaniesForEditing(initialCompanyEntries));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const refreshFromPerson = useCallback((person, snapshot, companies) => {
    setEditedDescription(person?.description ?? '');
    setEditedExperience(person?.experience ?? '');
    setEditedRole(person?.role ?? '');
    setEditableSkills(snapshot?.list ?? []);
    setCompanyEntries(normalizeCompaniesForEditing(companies));
  }, []);

  useEffect(() => {
    if (!isEditing) {
      refreshFromPerson(initialPerson, skillSnapshot, initialCompanyEntries);
    }
  }, [initialPerson, skillSnapshot, initialCompanyEntries, isEditing, refreshFromPerson]);

  const startEditing = useCallback(
    (person, snapshot, companies) => {
      refreshFromPerson(person, snapshot, companies);
      setIsEditing(true);
      setSaveError(null);
    },
    [refreshFromPerson]
  );

  const cancelEditing = useCallback(
    (person, snapshot, companies) => {
      refreshFromPerson(person, snapshot, companies);
      setIsEditing(false);
      setSaveError(null);
      setIsSaving(false);
    },
    [refreshFromPerson]
  );

  const handleSkillSelect = useCallback((selectedOption, selectableSkills) => {
    if (!selectedOption) {
      return;
    }
    const newSkillId = selectedOption.value;
    const catalogSkill = selectableSkills.find((skill) => String(skill.skillid) === String(newSkillId));
    if (!catalogSkill) {
      return;
    }
    setEditableSkills((prev) => {
      if (prev.some((skill) => String(skill.skillid) === String(newSkillId))) {
        return prev;
      }
      return [
        {
          skillid: catalogSkill.skillid,
          skillname: catalogSkill.skillname,
          type: catalogSkill.type ?? null,
          level: null
        },
        ...prev
      ];
    });
  }, []);

  const handleSkillRemove = useCallback((skillId) => {
    setEditableSkills((prev) => prev.filter((skill) => String(skill.skillid) !== String(skillId)));
  }, []);

  const handleLevelChange = useCallback((skillId, nextValue, selectableSkills = []) => {
    setEditableSkills((prev) => {
      if (nextValue === 'none') {
        return prev.filter((skill) => String(skill.skillid) !== String(skillId));
      }

      const skillExists = prev.some((skill) => String(skill.skillid) === String(skillId));
      const nextLevel = nextValue === '' || nextValue === 'unknown' ? null : Number(nextValue);

      if (!skillExists) {
        const catalogSkill = selectableSkills.find((skill) => String(skill.skillid) === String(skillId));
        if (!catalogSkill) {
          return prev;
        }
        return [
          ...prev,
          {
            skillid: catalogSkill.skillid,
            skillname: catalogSkill.skillname,
            type: catalogSkill.type ?? null,
            level: nextLevel
          }
        ];
      }

      return prev.map((skill) => {
        if (String(skill.skillid) !== String(skillId)) {
          return skill;
        }
        return {
          ...skill,
          level: nextLevel
        };
      });
    });
  }, []);

  const handleCompanyFieldChange = useCallback((index, field, value) => {
    setCompanyEntries((prev) =>
      prev.map((entry, idx) => {
        if (idx !== index) {
          return entry;
        }
        return {
          ...entry,
          [field]: value
        };
      })
    );
  }, []);

  const handleCompanyAdd = useCallback(() => {
    setCompanyEntries((prev) => [...prev, createEmptyCompanyEntry()]);
  }, []);

  const handleCompanyRemove = useCallback((index) => {
    setCompanyEntries((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback(() => {
    const normalizedSkills = diffSkillsForPayload(editableSkills);
    const companiesPayload = sanitizeCompaniesForPayload(companyEntries);
    return {
      description: editedDescription ?? null,
      experience: editedExperience ?? null,
      role: editedRole?.trim() ? editedRole.trim() : null,
      skills: normalizedSkills,
      companies: companiesPayload
    };
  }, [editableSkills, companyEntries, editedDescription, editedExperience, editedRole]);

  const syncFromPerson = useCallback(
    (person, snapshot, companies) => {
      refreshFromPerson(person, snapshot, companies);
    },
    [refreshFromPerson]
  );

  return {
    isEditing,
    setIsEditing,
    editedDescription,
    setEditedDescription,
    editedExperience,
    setEditedExperience,
    editedRole,
    setEditedRole,
    editableSkills,
    setEditableSkills,
    companyEntries,
    setCompanyEntries,
    startEditing,
    cancelEditing,
    handleSkillSelect,
    handleSkillRemove,
    handleLevelChange,
    handleCompanyFieldChange,
    handleCompanyAdd,
    handleCompanyRemove,
    buildPayload,
    syncFromPerson,
    isSaving,
    setIsSaving,
    saveError,
    setSaveError
  };
};

export default useEditableProfile;
